import { useState } from "react";
import { Link } from "react-router-dom";
import { Shield, ChatCircle, Heart, ChartLineUp, ArrowSquareOut, CaretDown, CaretUp, PencilLine, FirstAidKit } from "@phosphor-icons/react";
import AppNav from "../components/AppNav";
import AppFooter from "../components/AppFooter";
import PageMeta from "../components/PageMeta";
import JsonLd, { collectionPageSchema } from "../components/JsonLd";

export default function Advocacy() {
  const [showResearchOrgs, setShowResearchOrgs] = useState(false);

  const researchOrganizations = [
    { name: "Endometriosis Foundation of America (EndoFound)", url: "https://www.endofound.org/", description: "Funds research to improve the standard of care, offering grants up to $50,000 for endometriosis studies." },
    { name: "World Endometriosis Research Foundation (WERF)", url: "https://endometriosis.ca/", description: "Focuses on global collaboration to enhance medical research and surgical outcomes." },
    { name: "NIH / NICHD", url: "https://www.nichd.nih.gov/", description: "A major public funder supporting research into PCOS causes, diagnostics, and treatments." },
    { name: "Endometriosis Research Center (ERC)", url: "https://www.endocenter.org/", description: "A non-profit supporting studies to find a cure and improve quality of life." },
    { name: "Office on Women's Health (OWH)", url: "https://www.womenshealth.gov/", description: "Administers grants for innovative women's health projects." },
    { name: "Society for Women's Health Research (SWHR)", url: "https://swhr.org/", description: "Promotes research on sex and gender differences in disease." },
    { name: "AthenaDAO", url: "https://www.athenadao.co/", description: "A decentralized organization funding women's health research." },
  ];

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-orange-50 to-white dark:from-gray-900 dark:to-gray-900"
    >
      <PageMeta
        title="Women's Health Advocacy Tools"
        description="Self-advocacy tools for women's health. Generate insurance appeal letters, prepare for doctor appointments, and find communication guides."
        path="/advocacy"
      />
      <JsonLd data={collectionPageSchema({ name: 'Health Advocacy Tools', description: 'Self-advocacy tools and resources for women\'s health. Generate insurance appeal letters, prepare for appointments, and find communication guides.', url: 'https://romyhealth.co/advocacy' })} />
      <AppNav />

      {/* Main Content */}
      <main className="pt-16 pb-16 md:pb-0 min-h-screen">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="flex justify-center mb-4">
              <Shield size={80} className="text-orange-500" weight="regular" />
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold mb-4">
              <span className="text-pink-500">Advocacy</span>{" "}
              <span className="text-gray-800 dark:text-gray-100">Center</span>
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
              Empower yourself with tools, templates, and resources to navigate
              healthcare with confidence. Your health, your voice, your rights.
            </p>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            {/* Appointment Prep */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-8 border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-shadow">
              <div className="flex justify-center mb-6">
                <FirstAidKit size={64} className="text-orange-500" weight="regular" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4 text-center">
                Appointment Prep
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6 text-center">
                Track appointments, get AI-powered briefings with talking points
                and questions, and download a prep sheet for your visit.
              </p>
              <div className="flex justify-center">
                <Link
                  to="/health/appointments"
                  className="px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all shadow-lg hover:shadow-xl inline-block"
                >
                  Manage Appointments
                </Link>
              </div>
            </div>

            {/* Communication Guides */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-8 border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-shadow">
              <div className="flex justify-center mb-6">
                <ChatCircle size={64} className="text-pink-500" weight="fill" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4 text-center">
                Communication Guides
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6 text-center">
                Learn effective communication strategies for discussing symptoms,
                requesting tests, and advocating for proper care with healthcare
                providers.
              </p>
              <div className="flex justify-center">
                <Link
                  to="/communication-guides"
                  className="px-6 py-3 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 font-semibold rounded-lg border-2 border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 transition-all"
                >
                  Explore Guides
                </Link>
              </div>
            </div>
          </div>

          {/* AI-Powered Tools */}
          <div className="max-w-lg mx-auto mb-16">
            {/* Advocacy Letter Generator */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-8 border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-shadow">
              <div className="flex justify-center mb-6">
                <PencilLine size={64} className="text-orange-500" weight="regular" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4 text-center">
                Advocacy Letter Generator
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6 text-center">
                Generate professional letters for insurance appeals, employer accommodations, or provider referral requests.
              </p>
              <div className="flex justify-center">
                <Link
                  to="/advocacy/letter-generator"
                  className="px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all shadow-lg hover:shadow-xl inline-block"
                >
                  Generate Letter
                </Link>
              </div>
            </div>
          </div>

          {/* Self-Advocacy Tools Section */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold text-gray-800 dark:text-gray-100 mb-8 text-center">
              Self-Advocacy Tools
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <Link
                to="/chat?prefill=Can you explain [your diagnosis or condition] in plain language — what it means, how it's diagnosed, and what I should know?"
                className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 border border-orange-200 dark:border-orange-800 hover:border-orange-400 hover:shadow-lg transition-all cursor-pointer"
              >
                <div className="flex justify-center mb-4">
                  <Shield size={40} className="text-orange-500" weight="regular" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2 text-center">
                  Understanding Your Diagnosis
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm text-center">
                  Plain-language explanations of common conditions, tests, and
                  treatments.
                </p>
              </Link>
              <Link
                to="/chat?prefill=What are my treatment options for [list your diagnosis here]?"
                className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 border border-orange-200 dark:border-orange-800 hover:border-orange-400 transition-colors block"
              >
                <div className="flex justify-center mb-4">
                  <ChartLineUp size={40} className="text-orange-500" weight="regular" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2 text-center">
                  Treatment Options Explorer
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm text-center">
                  Compare treatment options with evidence-based information.
                </p>
              </Link>
            </div>
          </section>

          {/* Know Your Healthcare Rights Section */}
          <section className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Left: Healthcare Rights */}
              <div>
                <h2 className="text-4xl font-bold text-gray-800 dark:text-gray-100 mb-6">
                  Know Your Healthcare Rights
                </h2>
                <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6">
                  You Have the Right To:
                </h3>
                <ul className="space-y-4">
                  {[
                    "Get a second opinion",
                    "Access your medical records",
                    "Refuse or stop treatment",
                    "Ask questions and get clear answers",
                    "Be treated with respect and dignity",
                    "Request a different healthcare provider",
                    "Privacy and confidentiality",
                  ].map((right, index) => (
                    <li key={index} className="flex items-center space-x-3">
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-pink-500 text-white flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </span>
                      <span className="text-gray-700 dark:text-gray-300">{right}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Right: Questions to Ask */}
              <div className="bg-pink-50 dark:bg-pink-950 rounded-2xl p-8 shadow-md">
                <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-6">
                  Questions to Ask Your Doctor
                </h2>
                <ul className="space-y-3 mb-6">
                  {[
                    "What is my diagnosis and what does it mean?",
                    "What are all my treatment options?",
                    "What are the potential risks and benefits?",
                    "What happens if I don't treat this?",
                    "How will this affect my daily life?",
                    "What tests do I need and why?",
                  ].map((question, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <span className="flex-shrink-0 w-2 h-2 rounded-full bg-orange-500 mt-2"></span>
                      <span className="text-gray-700 dark:text-gray-300">{question}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/chat"
                  className="w-full px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
                >
                  <ChatCircle size={20} weight="fill" />
                  <span>Ask Romy to help</span>
                </Link>
              </div>
            </div>
          </section>

          {/* Advocate for Women's Health Research Section */}
          <section className="bg-pink-50 dark:bg-pink-950 rounded-2xl p-8 mb-16">
            <div className="flex items-center space-x-3 mb-4">
              <ChatCircle size={32} className="text-pink-500" weight="fill" />
              <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">
                Advocate for Women's Health Research
              </h2>
            </div>
            <p className="text-gray-700 dark:text-gray-300 mb-8 text-lg">
              Women's health conditions are historically underfunded and
              under-researched. Learn how you can make a difference.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
              <div>
                <button
                  onClick={() => setShowResearchOrgs(!showResearchOrgs)}
                  className="w-full bg-white dark:bg-gray-900 rounded-xl p-6 border border-orange-200 dark:border-orange-800 hover:border-orange-400 transition-colors text-left cursor-pointer"
                >
                  <div className="flex justify-center mb-4">
                    <Heart size={40} className="text-orange-500" weight="regular" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2 text-center">
                    Support Research Initiatives
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm text-center">
                    Find organizations funding critical research in endometriosis,
                    PCOS, fertility, and more.
                  </p>
                  <div className="flex justify-center mt-3 text-orange-500">
                    {showResearchOrgs ? <CaretUp size={20} /> : <CaretDown size={20} />}
                  </div>
                </button>
                {showResearchOrgs && (
                  <div className="mt-3 bg-white dark:bg-gray-900 rounded-xl border border-orange-200 dark:border-orange-800 divide-y divide-orange-100 dark:divide-orange-800">
                    {researchOrganizations.map((org) => (
                      <a
                        key={org.name}
                        href={org.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-start gap-3 p-4 hover:bg-orange-50 dark:hover:bg-orange-950 transition-colors first:rounded-t-xl last:rounded-b-xl"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-gray-800 dark:text-gray-100 text-sm">{org.name}</div>
                          <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">{org.description}</p>
                        </div>
                        <ArrowSquareOut size={16} className="text-orange-400 mt-1 flex-shrink-0" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
              <a
                href="https://harvardpolitics.com/increase-funding-on-womens-health/#:~:text=A%20leading%20cause%20of%20the,received%20$3.1%20billion%20in%20funding"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white dark:bg-gray-900 rounded-xl p-6 border border-orange-200 dark:border-orange-800 hover:border-orange-400 transition-colors block"
              >
                <div className="flex justify-center mb-4">
                  <ChartLineUp size={40} className="text-orange-500" weight="regular" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2 text-center">
                  The Funding Gap
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm text-center">
                  Understand why women's health receives less research funding
                  and how to advocate for change.
                </p>
                <div className="flex justify-center mt-3">
                  <ArrowSquareOut size={16} className="text-orange-400" />
                </div>
              </a>
            </div>
          </section>

          {/* Get Involved Section */}
          <section className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 dark:text-gray-100 mb-4">
              Get Involved
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-3xl mx-auto">
              Your voice matters. Join advocacy efforts and help shape the
              future of women's healthcare.
            </p>
            <a
              href="https://www.change.org/p/close-the-research-gap"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-8 py-4 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all shadow-lg hover:shadow-xl text-lg"
            >
              Sign Petitions
            </a>
          </section>
        </div>
      </main>

      <AppFooter />
    </div>
  );
}
